import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { useCurrentProcessor } from 'src/app/queries/processor.queries';
import { useCurrentMerchant } from 'src/app/queries/merchant.queries';

import useAuthUser from '../../hooks/useAuthUser';

function UserMenu() {
  const user = useAuthUser();
  const [userMenu, setUserMenu] = useState(null);

  const processorQuery = useCurrentProcessor({ enabled: user?.role === 'processor' });
  const merchantQuery = useCurrentMerchant({ enabled: user?.role === 'merchant' });

  const processor = processorQuery.data?.data;
  const merchant = merchantQuery.data?.data;

  const email = useMemo(() => {
    return processor?.user?.email || merchant?.user?.email || user?.email || user?.name || '';
  }, [merchant?.user?.email, processor?.user?.email]);

  const displayName = useMemo(() => {
    if (processor) {
      return processor.companyName;
    }
    if (merchant) {
      return merchant.firstName && merchant.lastName
        ? `${merchant.firstName} ${merchant.lastName}`
        : merchant.businessName;
    }
    return user?.name || email.split('@')[0] || '';
  }, [processor, merchant, user?.name, email]);

  const userMenuClick = (event) => {
    setUserMenu(event.currentTarget);
  };

  const userMenuClose = () => {
    setUserMenu(null);
  };

  return (
    <>
      <Button
        className="min-h-40 min-w-40 px-0 md:px-16 py-0 md:py-6"
        onClick={userMenuClick}
        color="inherit"
      >
        <div className="hidden md:flex flex-col mx-4 items-end">
          <Typography component="span" className="font-semibold flex">
            {displayName}
          </Typography>
          <Typography className="text-11 font-medium capitalize" color="text.secondary">
            {user?.role ? user.role.toString() : 'Guest'}
          </Typography>
        </div>

        {user?.picture ? (
          <Avatar className="md:mx-4" alt="user photo" src={user.picture} />
        ) : (
          <Avatar className="md:mx-4">{user?.email.split('@')[0]}</Avatar>
        )}
      </Button>

      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: 'py-8',
        }}
      >
        <MenuItem
          component={Link}
          to={`${user?.role}/profile`}
          onClick={userMenuClose}
          role="button"
        >
          <ListItemIcon className="min-w-40">
            <FuseSvgIcon>heroicons-outline:user-circle</FuseSvgIcon>
          </ListItemIcon>
          <ListItemText primary="My Profile" />
        </MenuItem>
        <MenuItem
          component={NavLink}
          to="/sign-out"
          onClick={() => {
            userMenuClose();
          }}
        >
          <ListItemIcon className="min-w-40">
            <FuseSvgIcon>heroicons-outline:logout</FuseSvgIcon>
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Popover>
    </>
  );
}

export default UserMenu;
